import type { BBox, DetectionItem } from '@EcamV2/api';
import { CameraPhotos, CameraPhotoVehicles } from '@EcamModel/model';
import {
    pickTrackedDetection as pickMatchingDetection,
    type TrackReference as VehicleReference,
} from '@pages/ecam-v2/components/vehicle-track-match';
import { normalizePlateText, warpedBoxToBBox } from '@pages/ecam-v2/utils/bbox-utils';
import type { PCInfo } from '..';
import { findVehicleWarpedBox, pcImage } from '../components/EvidencePhotoGroup';

/** Everything needed to pick the PC's vehicle out of a live photo — computed once per PC. */
export type PcVehicleContext = {
    refs: VehicleReference[];
    evidenceVehicleIds: Set<number>;
};

export type PcVehicleInPhoto = { vehicle: CameraPhotoVehicles; vehicleBbox: BBox | null };

type VehicleDetectionPair = { vehicle: CameraPhotoVehicles; detection: DetectionItem };

const toMs = (d?: Date | string | null): number => (d ? new Date(d).getTime() : NaN);

/** Each live-photo vehicle carries its own raw AI detection (`AiPlateCandidate`); vehicles without one are skipped. */
function toDetectionItems(photo: CameraPhotos): VehicleDetectionPair[] {
    return (photo.CameraPhotoVehicles ?? []).flatMap((vehicle) => {
        const c = vehicle.AiPlateCandidate;
        if (!c) return [];
        const detection: DetectionItem = {
            car: { confidence: c.car?.confidence ?? 0, warpedBox: c.car?.warpedBox ?? [] },
            text: c.text ?? '',
            confidence: c.confidence ?? [],
            warpedBox: c.warpedBox ?? [],
            expandedBox: c.expanededBox ?? [],
        };
        return [{ vehicle, detection }];
    });
}

/**
 * References mirroring ReviewDetail's ContextTimeline (ECAM-5558): entry/grace/exit evidence
 * vehicle boxes for IoU matching, plus the PC's verified plate as the only plate-text reference
 * (evidence OCR text can be null/garbage/stale).
 */
export function buildPcVehicleContext(pc: PCInfo, evidenceVehicleIds: Set<number>): PcVehicleContext {
    const { entryPhoto, validationPhoto, exitPhoto } = pcImage(pc);
    const refs: VehicleReference[] = [entryPhoto, validationPhoto, exitPhoto].flatMap((v) => {
        const box = v ? findVehicleWarpedBox(v) : undefined;
        const capturedAt = toMs(v?.CameraPhoto?.CaptureAt);
        if (!box || isNaN(capturedAt)) return [];
        return [{ capturedAt, plateText: null, vehicleBbox: warpedBoxToBBox(box) }];
    });
    const plateText = normalizePlateText(pc.Plate) || null;
    if (plateText) refs.push({ capturedAt: 0, plateText, vehicleBbox: null });

    return { refs, evidenceVehicleIds };
}

const sameBBox = (a: BBox | null, b: BBox | null) =>
    !!a && !!b && a.x1 === b.x1 && a.y1 === b.y1 && a.x2 === b.x2 && a.y2 === b.y2;

/**
 * The single vehicle in `photo` that belongs to the PC, or null (caller draws nothing).
 * An evidence vehicle is trusted directly; otherwise the shared ReviewDetail matcher decides.
 */
export function findPcVehicleInPhoto(photo: CameraPhotos, ctx: PcVehicleContext): PcVehicleInPhoto | null {
    const evidenceVehicle = photo.CameraPhotoVehicles?.find((v) => v.Id != null && ctx.evidenceVehicleIds.has(v.Id));
    if (evidenceVehicle) {
        const box = findVehicleWarpedBox(evidenceVehicle);
        return { vehicle: evidenceVehicle, vehicleBbox: box ? warpedBoxToBBox(box) : null };
    }

    const t = toMs(photo.CaptureAt);
    if (isNaN(t)) return null;
    const pairs = toDetectionItems(photo);
    // No entry–exit window restriction for PCs: the box fallback is always allowed.
    const picked = pickMatchingDetection(
        pairs.map((p) => p.detection),
        ctx.refs,
        t,
        true
    );
    if (!picked?.vehicleBbox) return null;

    // The matcher returns a fresh object — map back to the owning vehicle by re-deriving each
    // candidate's vehicle box exactly as the matcher does (car box, else plate box).
    const owner = pairs.find((p) =>
        sameBBox(
            warpedBoxToBBox(p.detection.car.warpedBox) ?? warpedBoxToBBox(p.detection.warpedBox),
            picked.vehicleBbox
        )
    );
    return owner ? { vehicle: owner.vehicle, vehicleBbox: picked.vehicleBbox } : null;
}

export const bboxToRect = (b: BBox) => ({ x: b.x1, y: b.y1, width: b.x2 - b.x1, height: b.y2 - b.y1 });
